<script setup lang="ts">
import { FingerPrintIcon } from '@heroicons/vue/outline';
import { BellIcon, FireIcon } from '@heroicons/vue/solid';
import { differenceInMilliseconds, intlFormat, parseISO } from 'date-fns';
import type { PropType } from 'vue';
import { addressGradientStyles, avatarGradientStyles } from '~/utilities/avatar-gradient';
import { formatXchPrice } from '~/utilities/chia-display-utils';

const props = defineProps({
  nft: { type: Object as PropType<any>, required: true },
});
const { nft } = toRefs(props);

const showCollapsed = ref(false);

const timeline = computed(() => {
  const auctionStartEvents = nft.value.auctions
    .filter((auction: any) => differenceInMilliseconds(parseISO(auction.start), new Date()) < 0)
    .map((auction: any) => ({
      timestamp: auction.start,
      owner: undefined,
      address: undefined,
      type: 5,
    }));
  const auctionEndEvents = nft.value.auctions
    .filter((auction: any) => differenceInMilliseconds(parseISO(auction.overtime.end), new Date()) < 0)
    .map((auction: any) => ({
      timestamp: auction.overtime.end,
      owner: undefined,
      address: undefined,
      type: 6,
      xch_price: auction.highest_bid?.xch_price,
    }));

  return [...nft.value.events, ...auctionStartEvents, ...auctionEndEvents]
    .sort((a, b) =>
      a.event_index && b.event_index
        ? a.event_index - b.event_index
        : differenceInMilliseconds(parseISO(a.timestamp), parseISO(b.timestamp))
    )
    .map((event) => {
      const datetime = parseISO(event.timestamp);
      let content = 'provenance.transferredTo';
      let xchPrice;
      let payments;
      const owner = event.owner;
      let address = event.address;
      const minter = undefined;
      if (event.type === 0) {
        content = 'provenance.mintedBy';
      } else if (event.type === 5) {
        content = 'provenance.auctionStarted';
      } else if (event.type === 6) {
        if (event.xch_price) {
          content = 'provenance.auctionEnded';
        } else {
          content = 'provenance.auctionEndedNoBid';
        }
        xchPrice = event.xch_price;
      } else if (event.type === 2) {
        content = 'provenance.acquiredBy';
        xchPrice = event.xch_price;
        if (event.payments) {
          payments = Object.entries(
            event.payments.reduce(
              (acc: any, payment: any) => ({
                ...acc,
                [payment.asset_id || 'xch']: (acc[payment.asset_id || 'xch'] || 0) + payment.amount,
              }),
              {}
            )
          )
            .map(([assetId, amount]) => ({
              assetId,
              amount,
            }))
            .sort(({ assetId: a }) => (a === 'xch' ? -1 : 1));
        }
      } else if (event.type === 3) {
        content = 'provenance.burned';
        address = undefined;
      }
      return {
        id: event.event_index,
        type: event.type,
        content,
        owner,
        minter,
        address,
        datetime,
        date: intlFormat(datetime, { dateStyle: 'medium', timeStyle: 'medium' } as any),
        xchPrice,
        payments,
      };
    })
    .reduce((acc: any, event: any) => {
      const previousEvent = acc && acc[acc.length - 1];
      if (
        !showCollapsed.value &&
        previousEvent &&
        previousEvent.type === 1 &&
        event.type === 1 &&
        (!event.owner || previousEvent.owner === event.owner)
      ) {
        return [
          ...acc.slice(0, -1),
          {
            ...previousEvent,
            date: event.date,
            collapsed: [...(previousEvent.collapsed || []), event],
          },
        ];
      }
      return [...acc, event];
    }, [])
    .reverse();
});

const navigateToEvent = (event: any) => {
  navigateTo(`/nfts/${nft.value.encoded_id}/events/${event.id}`);
};
</script>
<template>
  <div class="flow-root">
    <ul role="list" class="-mb-8">
      <li v-for="(event, eventIdx) in timeline" :key="event.id" class="cursor-pointer" @click="navigateToEvent(event)">
        <div class="relative pb-10">
          <span
            v-if="eventIdx !== timeline.length - 1"
            class="absolute left-5 top-5 -ml-px h-full w-0.5 bg-neutral-200 dark:bg-neutral-700"
            aria-hidden="true"
          />
          <div class="relative flex space-x-3">
            <div>
              <span
                :class="[
                  event.owner?.avatar_uri ? '' : 'bg-gradient-to-br',
                  event.type === 3 ? 'bg-red-600' : '',
                  event.type === 5 ? 'bg-emerald-600' : '',
                  event.type === 6 ? 'bg-emerald-600' : '',
                  'flex h-10 w-10 items-center justify-center rounded-full ring-8 ring-white dark:ring-black',
                ]"
                :style="
                  event.owner
                    ? { ...avatarGradientStyles(event.owner.id) }
                    : event.address
                    ? { ...addressGradientStyles(event.address.id) }
                    : {}
                "
              >
                <img
                  v-if="event.owner?.avatar_uri"
                  :src="event.owner.avatar_uri"
                  loading="lazy"
                  class="inline h-full w-full rounded-full object-cover"
                />
                <FingerPrintIcon v-else-if="event.owner" class="h-3/5 w-3/5 text-neutral-200 dark:text-neutral-900" />
                <FireIcon v-else-if="event.type === 3" class="h-3/5 w-3/5 text-white dark:text-neutral-100" />
                <BellIcon v-else-if="event.type === 5" class="h-3/5 w-3/5 text-white dark:text-neutral-100" />
                <BellIcon v-else-if="event.type === 6" class="h-3/5 w-3/5 text-white dark:text-neutral-100" />
              </span>
            </div>
            <div class="mt-0 flex flex-1 flex-col pt-1.5">
              <div>
                <span
                  class="flex min-w-[20rem] flex-wrap items-center gap-1 whitespace-nowrap text-neutral-600 dark:text-neutral-300"
                >
                  {{ $t(`${event.content}Pre`) }}
                  <ProfileHandle
                    v-if="event.owner"
                    :profile="event.owner"
                    avatar-dimension=""
                    named-classes="text-base font-bold text-neutral-700 dark:text-neutral-200"
                    unnamed-classes="text-base font-semibold tracking-tight font-mono text-neutral-900 dark:text-neutral-50"
                  />
                  <AddressHandle v-else-if="event.address" avatar-dimension="" :address="event.address" />
                  {{ $t(`${event.content}Post`) }}
                  <a
                    v-if="event.collapsed"
                    href="#"
                    class="hover:text-neutral-900 dark:text-neutral-50"
                    @click="
                      ($event) => {
                        $event.preventDefault();
                        $event.stopPropagation();
                        showCollapsed = !showCollapsed;
                      }
                    "
                  >
                    {{ $t('provenance.andOtherAddresses', { count: event.collapsed.length }) }}</a
                  >
                  <span v-else-if="event.type === 6 && event.xchPrice" class="inline-flex items-center">
                    <span class="inline-flex gap-1 font-semibold text-neutral-700 dark:text-neutral-200">
                      <img class="h-6 w-6" src="/chia_circle.png" />{{ formatXchPrice(event.xchPrice, true) }}
                    </span></span
                  >

                  <span v-else-if="event.payments"> for</span>

                  <span
                    v-for="(payment, index) in event.payments"
                    :key="payment.assetId"
                    class="inline-flex items-center"
                  >
                    <span v-if="index !== 0" class="mr-1">and</span>
                    <NuxtLink
                      v-if="payment.assetId !== 'xch'"
                      target="_blank"
                      :to="`https://dexie.space/assets/${payment.assetId}`"
                      class="inline-flex gap-1 font-semibold text-neutral-700 dark:text-neutral-200"
                    >
                      <img class="h-6 w-6 rounded-full" :src="`https://icons.dexie.space/${payment.assetId}.webp`" />{{
                        payment.amount / 1000
                      }}
                    </NuxtLink>
                    <span v-else class="inline-flex gap-1 font-semibold text-neutral-700 dark:text-neutral-200">
                      <img class="h-6 w-6" src="/chia_circle.png" />{{ formatXchPrice(payment.amount / 10 ** 12) }}
                    </span>
                  </span>
                </span>
              </div>
              <div class="whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                <time :datetime="event.datetime">{{
                  event.datetime > new Date(0) ? event.date : 'Not yet minted'
                }}</time>
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
